import React, { useMemo, useCallback } from 'react';
import { AutocompletePopover, type AutocompleteItem } from './autocomplete-popover';
import { StatusIcon } from './status-icon';
import { EntityStatus } from '@/types/entity.types';

interface Client {
  clientId: string;
  clientName: string;
  entityCount?: number;
  entities?: Entity[]; // Include entities from clients summary
}

interface Entity {
  entityId: string;
  entityName: string;
  type?: string;
  connectionStatus?: string;
  lastSync?: string;
}

interface UnifiedEntitySelectorProps {
  clients: Client[];
  entities: Entity[];
  selectedClientId: string | null;
  selectedEntityId: string | 'all' | null;
  onSelectionChange: (clientId: string | null, entityId: string | 'all' | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  allowAllEntities?: boolean;
  isHydrated?: boolean;
}

// Helper function to get entity status for status icons
function getEntityStatus(entity: Entity): EntityStatus | 'unknown' {
  // Handle both field name variations from different API endpoints
  const status = entity.connectionStatus || (entity as any).connection_status;
  return (status && Object.values(EntityStatus).includes(status as EntityStatus))
    ? status as EntityStatus
    : 'unknown';
}

// Helper to parse selection value
function parseSelection(value: string): { clientId: string; entityId: string } | null {
  if (!value || typeof value !== 'string') {
    console.error('Invalid selection value:', value);
    return null;
  }
  
  const parts = value.split(':');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    console.error('Invalid selection format, expected "clientId:entityId":', value);
    return null;
  }
  
  return { clientId: parts[0], entityId: parts[1] };
}

export function UnifiedEntitySelector({
  clients,
  entities,
  selectedClientId,
  selectedEntityId,
  onSelectionChange,
  isLoading = false,
  disabled = false,
  placeholder = 'Select client & entity...',
  className,
  allowAllEntities = true,
  isHydrated = true,
}: UnifiedEntitySelectorProps) {

  // Determine current display name based on selection
  const currentDisplayName = useMemo(() => {
    if (!selectedClientId) return null;
    
    const selectedClient = clients.find(c => c.clientId === selectedClientId);
    if (!selectedClient) return 'Unknown client';
    
    if (!selectedEntityId || selectedEntityId === 'all') return selectedClient.clientName;
    
    const selectedEntity = entities.find(e => e.entityId === selectedEntityId);
    if (!selectedEntity) return selectedClient.clientName;
    
    // Use dashboard convention: single entity shows client name, multiple shows "Client | Entity"
    if (entities.length > 1) {
      return `${selectedClient.clientName} | ${selectedEntity.entityName}`;
    } else {
      return selectedClient.clientName;
    }
  }, [selectedClientId, selectedEntityId, clients, entities]);

  // Build dropdown items (simplified without grouping)
  const items = useMemo(() => {
    const result: AutocompleteItem[] = [];
    
    // Debug logging
    console.log('[UnifiedEntitySelector] Building dropdown items:', {
      selectedClientId,
      clientsCount: clients.length,
      entitiesCount: entities.length,
      clients: clients.map(c => ({ id: c.clientId, name: c.clientName, entityCount: c.entityCount })),
      entities: entities.map(e => ({ id: e.entityId, name: e.entityName }))
    });
    
    // Note: "All Entities" option removed per user request
    
    // Show all entities from all clients - simple and clean
    clients.forEach(client => {
      // Check if this client has entities from the clients summary data
      if (client.entities && client.entities.length > 0) {
        client.entities.forEach(entity => {
          const displayLabel = client.entities.length > 1 
            ? `${client.clientName} | ${entity.entityName}`
            : client.clientName;
            
          result.push({
            value: `${client.clientId}:${entity.entityId}`,
            label: displayLabel,
            icon: <StatusIcon status={getEntityStatus(entity)} showTooltip={true} />,
          });
        });
      }
    });
    
    return result;
  }, [clients]);

  // Handle selection from dropdown
  const handleSelect = useCallback((value: string) => {
    const parsed = parseSelection(value);
    if (!parsed) {
      console.error('Failed to parse selection value:', value);
      // Continue gracefully - don't show user error for this edge case
      return;
    }
    
    const { clientId, entityId } = parsed;
    
    if (entityId === 'switch') {
      // Switching to a new client - let the store handle loading entities
      onSelectionChange(clientId, null);
    } else {
      // Selecting a specific entity
      onSelectionChange(clientId, entityId);
    }
  }, [onSelectionChange]);

  // Get current selected value for the dropdown
  const selectedValue = useMemo(() => {
    if (selectedClientId && selectedEntityId && selectedEntityId !== 'all') {
      return `${selectedClientId}:${selectedEntityId}`;
    }
    return null;
  }, [selectedClientId, selectedEntityId]);

  // Custom button display
  const getButtonDisplay = () => {
    if (isLoading || !isHydrated) {
      return 'Loading...';
    }
    
    if (currentDisplayName) {
      return currentDisplayName;
    }
    
    return placeholder;
  };

  return (
    <AutocompletePopover
      items={items}
      selectedValue={isHydrated ? selectedValue : null}
      onSelect={handleSelect}
      placeholder={getButtonDisplay()}
      searchPlaceholder="Search clients and entities..."
      emptyMessage={
        selectedClientId 
          ? "No entities found for this client."
          : "No clients found."
      }
      isLoading={isLoading || !isHydrated}
      disabled={disabled || !isHydrated || items.length === 0}
      className={className}
      aria-label="Select client and entity"
      aria-busy={isLoading || !isHydrated}
      screenReaderLabel="Search for client and entity"
      buttonClassName="min-w-[200px] max-w-[300px] justify-start text-left"
      showCheckmarks={true}
    />
  );
}